import { useRef } from "react";

import {
  IColumn,
  ISelection,
  ScrollablePane,
  Selection,
  ShimmeredDetailsListProps,
  Stack,
  Text,
  useFormContext,
  useTheme
} from "@bps/fluent-ui";
import { QueryStateIndicator } from "@components/QueryStateIndicator";
import { ShimmeredDetailsList } from "@components/tables/ShimmeredDetailsList";
import {
  useSoftwarePackages,
  useSoftwarePublishers
} from "@libs/api/gateways/field/field-ops-gateway.hooks";

import { RolloutPackageFormValues } from "../../rollout/rolloutFormValues";
import {
  dependentSoftwarePackageTypes,
  excludedMaintenanceSoftwarePackageCodes,
  filterAndSortSoftwarePackages,
  getSoftwarePackageSummary,
  SelectSoftwarePackageFormValues,
  SelectSoftwarePackageSummaryDto
} from "./selectSoftwarePackageUtils";
import { SelectSoftwarePackageVersionFilterValues } from "./SelectSoftwarePackageVersionFilter";
import { SelectSoftwarePackageVersionTableRow } from "./SelectSoftwarePackageVersionTableRow";
import { SelectSoftwarePackageVersionDropDownHost } from "./version-drop-down";

export interface SelectSoftwarePackageVersionTableProps
  extends Pick<ShimmeredDetailsListProps, "selectionMode"> {
  filter: SelectSoftwarePackageVersionFilterValues;
  rolloutPackages?: RolloutPackageFormValues[];
}

export const SelectSoftwarePackageVersionTable = ({
  selectionMode,
  filter,
  rolloutPackages
}: SelectSoftwarePackageVersionTableProps) => {
  const { state, actions } = useFormContext();
  const theme = useTheme();
  const softwarePackageQuery = useSoftwarePackages({});
  const softwarePublishersQuery = useSoftwarePublishers();

  const isLoading =
    softwarePackageQuery.isLoading || softwarePublishersQuery.isLoading;

  const errorMessage =
    softwarePackageQuery?.error ?? softwarePublishersQuery?.error;

  const selection = useRef(
    new Selection<SelectSoftwarePackageFormValues>({
      getKey: item => item.softwarePackageId,
      onSelectionChanged: () => {
        const rowSelections = selection.current.getSelection();
        actions.setValue("packages", rowSelections);
      }
    })
  );

  const updateRolloutPackageVersion = (
    softwarePackageId: string,
    softwarePackageVersionId: string
  ) => {
    const updatedSoftwareVersions = [...state.values.packages];
    const existingSoftwareVersion = updatedSoftwareVersions.find(
      x => x.softwarePackageId === softwarePackageId
    );

    if (existingSoftwareVersion)
      existingSoftwareVersion.softwarePackageVersionId = softwarePackageVersionId;

    actions.setValue("packages", updatedSoftwareVersions);
  };

  // console.log("state", state);

  const columns: IColumn[] = [
    {
      key: "name",
      name: "Name",
      minWidth: 150,
      maxWidth: 800,
      isResizable: true,
      onRender: (dto: SelectSoftwarePackageSummaryDto) => (
        <Stack
          styles={{
            root: { whiteSpace: "initial" }
          }}
        >
          <Text>{dto.displayName}</Text>
          <Text variant="small">Description: {dto.description}</Text>
          <Text variant="small">Code: {dto.code}</Text>
        </Stack>
      )
    },
    {
      key: "version",
      name: "Version",
      minWidth: 200,
      maxWidth: 250,
      isResizable: true,
      onRender: (dto: SelectSoftwarePackageSummaryDto) => {
        const selection = state.values.packages?.find(
          x => x.softwarePackageId === dto.softwarePackageId
        );

        return (
          <SelectSoftwarePackageVersionDropDownHost
            selection={selection}
            softwarePackage={dto}
            onUpdateSoftwarePackageVersion={updateRolloutPackageVersion}
          />
        );
      }
    },
    {
      key: "softwarePackageType",
      name: "Software Type",
      minWidth: 100,
      maxWidth: 250,
      isResizable: true,
      onRender: (dto: SelectSoftwarePackageSummaryDto) =>
        dto.softwarePackageType
    },
    {
      key: "softwarePublisher",
      name: "Software Publisher",
      minWidth: 150,
      maxWidth: 150,
      isResizable: true,
      onRender: (dto: SelectSoftwarePackageSummaryDto) => dto.softwarePublisher
    }
  ];

  return (
    <QueryStateIndicator
      isLoading={isLoading}
      error={errorMessage}
      data={{
        softwarePackagesData: softwarePackageQuery.data,
        softwarePublishersData: softwarePublishersQuery.data
      }}
    >
      {({ softwarePackagesData, softwarePublishersData }) => {
        // Build our combined dto of all available software packages and their versions to pick from
        const getSoftwarePackageOptions = () => {
          if (!softwarePackagesData || !softwarePublishersData) return [];

          return getSoftwarePackageSummary(
            // Exclude software packages we already have in this rollout,
            // and also exclude dependent software package types.
            softwarePackagesData.filter(
              x =>
                !rolloutPackages?.find(
                  existing => existing.softwarePackageId === x.id
                ) &&
                !dependentSoftwarePackageTypes.includes(
                  x.softwarePackageType
                ) &&
                !excludedMaintenanceSoftwarePackageCodes.includes(x.code)
            ),
            softwarePublishersData
          );
        };

        const softwarePackageOptions = getSoftwarePackageOptions();

        if (softwarePackageOptions.length === 0) return null;

        return (
          <ScrollablePane
            styles={{ root: { height: 500, position: "relative" } }}
          >
            <ShimmeredDetailsList
              stickyHeader
              shimmerLines={5}
              columns={columns}
              onShouldVirtualize={() => true}
              items={filterAndSortSoftwarePackages(
                softwarePackageOptions,
                filter
              )}
              onRenderRow={row =>
                row ? <SelectSoftwarePackageVersionTableRow {...row} /> : null
              }
              detailsListStyles={{
                contentWrapper: {
                  ":hover": {
                    background: theme.palette.neutralQuaternaryAlt
                  }
                }
              }}
              selection={selection.current as ISelection}
              selectionPreservedOnEmptyClick
              setKey="software-package-version-table"
              selectionMode={selectionMode}
            />
          </ScrollablePane>
        );
      }}
    </QueryStateIndicator>
  );
};
